import { registerLocaleVariant } from "../registry";
import { makeId } from "../helpers";
import type { ArticleBody } from "../types";

// ─── Helper ─────────────────────────────────────────────────────────

function frMoonArticle(
  slug: string,
  variant: {
    title: string;
    subtitle: string;
    body: ArticleBody;
  },
): void {
  registerLocaleVariant(makeId(`moon-article-${slug}`), "fr", variant);
}

// ─── Articles ───────────────────────────────────────────────────────

frMoonArticle("how-embryos-are-graded", {
  title: "Notes d'embryons : les chiffres derrière la classification",
  subtitle:
    "Taux d'implantation par grade, et pourquoi le jour 5, 6 ou 7 compte.",
  body: {
    introduction: "Science de labo",
    quickSummary: [
      {
        title: "Évaluation visuelle.",
        description:
          "La classification évalue l'apparence de l'embryon — expansion, qualité de la masse cellulaire interne, et qualité du trophectoderme.",
      },
      {
        title: "Les notes ne sont pas un destin.",
        description:
          "Un blastocyste 4AA a la note visuelle la plus haute, mais des embryons 3BB donnent régulièrement des bébés en bonne santé.",
      },
      {
        title: "La subjectivité compte.",
        description:
          "La classification est subjective et varie d'un labo à l'autre — le même embryon pourrait recevoir des notes différentes selon les cliniques.",
      },
    ],
    sections: [
      {
        title: "Classification au jour 3",
        content:
          "Au jour 3 après la fécondation, les embryologistes évaluent l'embryon selon le nombre de cellules, la fragmentation, et l'uniformité des cellules. Un embryon idéal au jour 3 a entre 6 et 10 cellules de taille à peu près égale, avec une fragmentation minimale. La fragmentation — petits débris cellulaires entre les cellules — se mesure en pourcentage. Moins de 10 % est considéré excellent, 10 à 25 % est modéré, et au-dessus de 25 % est faible. Cependant, les notes au jour 3 sont moins prédictives que celles au jour 5, c'est pourquoi la plupart des cliniques cultivent maintenant les embryons jusqu'au stade blastocyste avant de prendre des décisions.",
        bulletPoints: [
          "Nombre de cellules idéal : 6 à 10 au jour 3",
          "Fragmentation sous 10 % est excellente",
          "L'uniformité des cellules (symétrie) est un indicateur positif",
          "Les notes au jour 3 sont moins prédictives que celles au jour 5",
        ],
      },
      {
        title: "La classification du blastocyste au jour 5 ou 6, expliquée",
        content:
          "La classification du blastocyste utilise un système à trois parties. La première est un chiffre de 1 à 6 indiquant l'expansion — combien l'embryon a grandi et s'est dilaté à l'intérieur (ou à l'extérieur) de sa coque. 1 est un blastocyste précoce avec une petite cavité ; 6 est complètement éclos. La deuxième est une lettre A à C notant la masse cellulaire interne (MCI), qui devient le bébé. A signifie beaucoup de cellules bien serrées, B plusieurs cellules regroupées plus lâchement, et C très peu de cellules. La troisième est une autre lettre A à C pour le trophectoderme — la couche externe de cellules qui devient le placenta. A signifie beaucoup de cellules formant une couche cohérente, B moins de cellules, et C très peu de grandes cellules.",
        bulletPoints: [
          "Chiffre 1 à 6 : niveau d'expansion et statut d'éclosion",
          "Première lettre A à C : masse cellulaire interne (devient le bébé)",
          "Deuxième lettre A à C : trophectoderme (devient le placenta)",
        ],
      },
      {
        title: "Ce qui fait un « bon » embryon",
        content:
          "Les notes 4AA, 4AB, et 4BA sont toutes considérées excellentes — blastocystes complètement dilatés avec une MCI et un trophectoderme bien notés. Mais ne sous-estime pas les notes plus basses. Des embryons notés 3BB ou même 3BC sont tout à fait transférables et donnent régulièrement des grossesses en bonne santé. La différence de taux de grossesse par transfert entre un 4AA et un 3BB est souvent plus petite que ce que les patientes pensent.",
      },
      {
        title: "Classification vs résultats PGT-A",
        content:
          "Voilà la partie surprenante : un embryon qui a une belle apparence peut être chromosomiquement anormal (aneuploïde), et un embryon d'apparence modeste peut être parfaitement normal (euploïde). L'apparence et la génétique ne se corrèlent pas toujours. C'est l'un des arguments clés en faveur du test PGT-A — il révèle des informations que le microscope ne peut tout simplement pas voir. Si tu choisis entre plusieurs embryons à transférer, les résultats PGT-A sont généralement plus prédictifs que la note visuelle seule.",
      },
      {
        title: "Ce que les données montrent vraiment",
        content:
          "Les taux de grossesse par transfert sont bien corrélés à la note, mais les différences sont plus nuancées que ce que la plupart des patientes imaginent. Les blastocystes de très haute note (4AA, 5AA) ont des taux d'implantation autour de 50 à 65 %, tandis que les embryons de note moyenne (3BB, 4BB) tombent généralement entre 35 et 50 %. Les embryons de notes plus basses ont encore des taux de réussite significatifs. À retenir : la note est une donnée, pas toute l'histoire.",
        bulletPoints: [
          "Très haute note (4AA, 5AA) : ~50 à 65 % d'implantation",
          "Note moyenne (3BB, 4BB) : ~35 à 50 % d'implantation",
          "Les notes plus basses ont encore des taux de réussite significatifs",
        ],
      },
      {
        title: "Jour 5 vs jour 6 vs jour 7",
        content:
          "Les embryons qui atteignent le stade blastocyste au jour 5 tendent à avoir des taux d'implantation légèrement plus élevés que ceux qui mettent jusqu'au jour 6 ou 7. Cependant, les blastocystes du jour 6 restent très viables et donnent couramment des grossesses en bonne santé. Les blastocystes du jour 7 sont moins courants et ont des taux de réussite plus faibles, mais certaines cliniques les cultivent et les congèlent — et des bébés naissent à partir d'embryons du jour 7. La vitesse de développement est une donnée de plus, pas un verdict.",
      },
    ],
    references: [
      {
        title:
          "Trophectoderm grade predicts outcomes of single-blastocyst transfers",
        authors: "Hill MJ, Richter KS, Heitmann RJ, et al.",
        journal: "Fertility and Sterility",
        year: 2013,
        url: "https://www.fertstert.org/article/S0015-0282(12)02541-9/fulltext",
        evidenceLevel: "cohort",
      },
      {
        title:
          "Correlation between standard blastocyst morphology, euploidy and implantation: an observational study in two centers",
        authors: "Capalbo A, Rienzi L, Cimadomo D, et al.",
        journal: "Human Reproduction",
        year: 2014,
        url: "https://academic.oup.com/humrep/article/29/6/1173/620894",
        evidenceLevel: "cohort",
      },
      {
        title: "Should extended blastocyst culture include day 7?",
        authors: "Hammond ER, Cree LM, Morbeck DE.",
        journal: "Human Reproduction",
        year: 2018,
        url: "https://academic.oup.com/humrep/article/33/3/343/4824588",
        evidenceLevel: "cohort",
      },
      {
        title: "The Istanbul consensus workshop on embryo assessment",
        authors:
          "Alpha Scientists in Reproductive Medicine and ESHRE Special Interest Group of Embryology",
        journal: "Human Reproduction",
        year: 2011,
        url: "https://academic.oup.com/humrep/article/26/6/1270/2913905",
        evidenceLevel: "guideline",
      },
    ],
  },
});

frMoonArticle("implantation-explained", {
  title: "L'implantation : ce qui se passe vraiment",
  subtitle: "La fenêtre de 72 heures où tout se joue.",
  body: {
    introduction: "Biologie",
    quickSummary: [
      {
        title: "Un processus sur plusieurs jours.",
        description:
          "L'implantation commence 6 à 10 jours après l'ovulation et prend environ 3 jours à se terminer.",
      },
      {
        title: "Trois étapes distinctes.",
        description:
          "L'embryon doit éclore de sa coque, s'attacher à l'endomètre, et envahir la muqueuse — chaque étape peut échouer indépendamment.",
      },
      {
        title: "L'échec est normal.",
        description:
          "Même avec un embryon parfait et un endomètre parfait, l'implantation échoue environ 30 à 40 % du temps — c'est de la biologie normale, pas un échec du traitement.",
      },
    ],
    sections: [
      {
        title: "La fenêtre d'implantation",
        content:
          "L'endomètre n'est réceptif à un embryon que pendant une fenêtre étroite — généralement les jours 20 à 24 d'un cycle naturel de 28 jours, soit environ 6 à 10 jours après l'ovulation. En dehors de cette fenêtre, l'endomètre résiste activement à l'attachement de l'embryon. Ce timing est contrôlé par la progestérone, qui transforme l'endomètre d'un état prolifératif à un état sécrétoire et réceptif. Dans les cycles de TEC médicamentés, les médecins ajustent soigneusement l'exposition à la progestérone pour ouvrir cette fenêtre au bon moment.",
      },
      {
        title: "L'éclosion",
        content:
          "Avant qu'un embryon ne puisse s'implanter, il doit d'abord éclore de la zone pellucide — la coque protéique protectrice qui l'entoure depuis l'état d'ovocyte. Au jour 5 ou 6 de son développement, le blastocyste en expansion casse naturellement cette coque. En FIV, les embryologistes pratiquent parfois une « éclosion assistée » en faisant une petite ouverture dans la zone, surtout pour les embryons à coque épaisse ou pour les patientes de plus de 38 ans.",
      },
      {
        title: "Apposition et adhésion",
        content:
          "Une fois éclos, l'embryon entre en contact initial avec la surface de l'endomètre — une phase appelée apposition. L'embryon repose librement sur la muqueuse, s'orientant de sorte que son trophectoderme fait face à la paroi utérine. Vient ensuite l'adhésion : un dialogue moléculaire entre l'embryon et l'endomètre verrouille l'embryon en place. Cette communication implique des intégrines, des sélectines et des cytokines — une poignée de main moléculaire complexe que les deux côtés doivent exécuter correctement.",
        bulletPoints: [
          "Apposition : l'embryon se positionne librement sur la muqueuse",
          "Adhésion : des signaux moléculaires verrouillent l'embryon en place",
          "L'embryon et l'endomètre doivent tous deux participer au dialogue",
        ],
      },
      {
        title: "L'invasion",
        content:
          "Après l'adhésion, les cellules du trophoblaste à la surface externe de l'embryon commencent à s'enfoncer dans l'endomètre. Ces cellules sont remarquablement agressives — elles envahissent la paroi utérine, remodèlent les vaisseaux sanguins maternels, et établissent l'apport sanguin précoce qui deviendra le placenta. Ce processus invasif produit la hCG (l'hormone de grossesse), et c'est pourquoi les tests de grossesse à domicile ne deviennent positifs qu'une fois l'implantation bien avancée.",
      },
      {
        title: "Pourquoi l'implantation échoue",
        content:
          "L'échec d'implantation est la plus grande source de perte de grossesse, et la cause la plus courante est une anomalie chromosomique de l'embryon — l'embryon n'a tout simplement pas le plan génétique pour se développer plus loin. Au-delà de ça, un décalage de timing de réceptivité (la fenêtre n'étant pas ouverte quand l'embryon arrive), un endomètre fin (sous 7 mm), et des facteurs immunitaires peuvent tous y contribuer. Dans beaucoup de cas, la cause exacte n'est jamais identifiée.",
        bulletPoints: [
          "L'anomalie chromosomique est la raison n° 1",
          "Décalage de timing de réceptivité entre embryon et muqueuse",
          "Un endomètre fin (sous 7 mm) réduit les taux d'implantation",
          "Facteurs immunitaires — réponse immunitaire trop active ou trop faible",
          "Souvent, la cause exacte n'est jamais identifiée",
        ],
      },
      {
        title: "Ce que la médecine peut et ne peut pas contrôler",
        content:
          "Les médecins peuvent optimiser l'endomètre avec œstrogène et progestérone, ajuster avec précision le timing du transfert, et sélectionner le meilleur embryon disponible. Ce qu'ils ne peuvent pas contrôler, c'est la conversation moléculaire entre l'embryon et l'endomètre, ou si un embryon spécifique a l'intégrité chromosomique pour s'implanter. C'est pour cela que même des transferts parfaitement exécutés échouent — et qu'un transfert qui n'a pas marché ne veut pas dire que quelque chose s'est mal passé. Souvent, ça veut dire que la biologie a fait ce que la biologie fait.",
      },
    ],
    references: [
      {
        title: "Implantation and the survival of early pregnancy",
        authors: "Norwitz ER, Schust DJ, Fisher SJ.",
        journal: "New England Journal of Medicine",
        year: 2001,
        url: "https://www.nejm.org/doi/full/10.1056/NEJMra000763",
        evidenceLevel: "review",
      },
      {
        title:
          "Endometrial thickness and pregnancy rates after IVF: a systematic review and meta-analysis",
        authors: "Kasius A, Smit JG, Torrance HL, et al.",
        journal: "Human Reproduction Update",
        year: 2014,
        url: "https://academic.oup.com/humupd/article/20/4/530/725823",
        evidenceLevel: "meta-analysis",
      },
      {
        title: "The human endometrium as a sensor of embryo quality",
        authors: "Macklon NS, Brosens JJ.",
        journal: "Biology of Reproduction",
        year: 2014,
        url: "https://academic.oup.com/biolreprod/article/91/4/98/2434923",
        evidenceLevel: "review",
      },
    ],
  },
});

frMoonArticle("ohss-explained", {
  title: "Le SHO : ce que c'est et comment l'éviter",
  subtitle:
    "Comprendre l'hyperstimulation ovarienne — risques, prévention, et quand appeler ton médecin.",
  body: {
    introduction: "Sécurité",
    quickSummary: [
      {
        title: "Généralement léger.",
        description:
          "Un SHO léger est courant (ballonnements, inconfort) et se résorbe seul. Un SHO sévère est rare mais grave.",
      },
      {
        title: "Surveillé activement.",
        description:
          "Ton médecin surveille le risque de SHO et peut ajuster ton protocole pour l'éviter.",
      },
      {
        title: "Les protocoles modernes aident.",
        description:
          "La stratégie « tout congeler » avec un déclenchement agoniste de la GnRH a réduit drastiquement les SHO sévères en FIV moderne.",
      },
    ],
    sections: [
      {
        title: "Ce qu'est le SHO",
        content:
          "Le syndrome d'hyperstimulation ovarienne (SHO) survient quand les ovaires sur-répondent aux médicaments de fertilité. Les ovaires gonflent, et du liquide passe des vaisseaux sanguins vers la cavité abdominale (et parfois le thorax). Dans les cas légers, cela provoque ballonnements et inconfort qui se résorbent en une semaine. Dans les cas sévères — rares avec les protocoles modernes — cela peut causer des complications dangereuses nécessitant une hospitalisation.",
      },
      {
        title: "Symptômes légers vs modérés vs sévères",
        content:
          "Le SHO existe sur un spectre. Savoir où tu te situes t'aide à reconnaître quand appeler ta clinique et quand foncer aux urgences.",
        bulletPoints: [
          "Léger : ballonnements, légère gêne abdominale, légère prise de poids (le plus courant, se résorbe en quelques jours)",
          "Modéré : nausées, vomissements, distension abdominale, prise de 2 kg ou plus en 1 à 2 jours",
          "Sévère : difficulté à respirer, douleur abdominale intense, caillots sanguins, diminution de la diurèse, dysfonction rénale (rare — nécessite une prise en charge médicale immédiate)",
        ],
      },
      {
        title: "Qui est à risque",
        content:
          "Certains facteurs rendent le SHO plus probable. Ton médecin les évalue avant de commencer la stimulation et ajuste le protocole en conséquence. Être à risque plus élevé ne veut pas dire que tu vas développer un SHO — ça veut dire que ton équipe te surveillera de plus près et prendra des mesures préventives.",
        bulletPoints: [
          "Jeune âge (moins de 35 ans)",
          "IMC bas",
          "Diagnostic de PMOS (ex SOPK)",
          "AMH élevée (au-dessus de 3,5 ng/mL)",
          "Comptage des follicules antraux élevé (au-dessus de 20)",
          "Taux d'œstradiol élevés pendant la stimulation",
          "Antécédent de SHO",
        ],
      },
      {
        title: "Comment les médecins préviennent le SHO",
        content:
          "La prévention est la partie la plus importante de la gestion du SHO, et les médecins modernes de la reproduction ont plusieurs outils à disposition. Le plus grand changement a été l'usage d'un déclenchement par agoniste de la GnRH (déclenchement Lupron) à la place du déclenchement hCG traditionnel. C'est l'hCG qui provoque le SHO — elle stimule les ovaires à continuer de produire liquide et hormones après la ponction. Un déclenchement Lupron évite ça entièrement.",
        bulletPoints: [
          "Protocole antagoniste avec déclenchement agoniste GnRH (Lupron) au lieu d'hCG",
          "Cycle « tout congeler » — évite l'hCG liée à la grossesse, qui aggrave le SHO",
          "Doses de médicaments plus faibles dès le départ pour les patientes à haut risque",
          "Coasting — arrêt des médicaments de stimulation pendant 1 à 2 jours avant le déclenchement",
          "Cabergoline — un agoniste dopaminergique qui réduit la perméabilité vasculaire",
        ],
      },
      {
        title: "Que faire si tu as des symptômes",
        content:
          "Après la ponction, certains ballonnements et de l'inconfort sont normaux — la plupart des patientes ont des symptômes légers. Mais tu dois appeler ta clinique si tu prends plus de 1 kg en une journée, si tu as des nausées ou vomissements persistants, ou si tu remarques que ton abdomen est très distendu. Va aux urgences si tu as une difficulté à respirer, une douleur intense, ou une diurèse diminuée. N'attends pas que ça passe tout seul — une intervention précoce fait une vraie différence.",
      },
      {
        title: "La bonne nouvelle",
        content:
          "Les SHO sévères ont chuté drastiquement avec les protocoles modernes. La combinaison des protocoles antagonistes, des déclenchements Lupron, et des stratégies « tout congeler » a rendu les SHO sévères de plus en plus rares dans les cliniques expérimentées. Beaucoup de centres à fort volume rapportent des taux de SHO sévère sous 1 %. Si ton médecin t'identifie comme à risque, les ajustements de protocole décrits ci-dessus sont très efficaces pour te garder en sécurité tout en obtenant un bon nombre d'ovocytes.",
      },
    ],
    references: [
      {
        title:
          "Gonadotropin-releasing hormone agonist versus HCG for oocyte triggering in antagonist-assisted reproductive technology",
        authors: "Youssef MA, Van der Veen F, Al-Inany HG, et al.",
        journal: "Cochrane Database of Systematic Reviews",
        year: 2014,
        url: "https://www.cochranelibrary.com/cdsr/doi/10.1002/14651858.CD008046.pub4/full",
        evidenceLevel: "meta-analysis",
      },
      {
        title:
          "Dopamine agonists for preventing ovarian hyperstimulation syndrome",
        authors: "Tang H, Mourad S, Zhai SD, Hart RJ.",
        journal: "Cochrane Database of Systematic Reviews",
        year: 2016,
        url: "https://www.cochranelibrary.com/cdsr/doi/10.1002/14651858.CD008605.pub3/full",
        evidenceLevel: "meta-analysis",
      },
      {
        title:
          "Prevention and treatment of moderate and severe ovarian hyperstimulation syndrome: a guideline",
        authors: "Practice Committee of the ASRM",
        journal: "Fertility and Sterility",
        year: 2016,
        url: "https://www.asrm.org/practice-guidance/practice-committee-documents/prevention-and-treatment-of-moderate-and-severe-ovarian-hyperstimulation-syndrome-a-guideline-2016/",
        evidenceLevel: "guideline",
      },
      {
        title:
          "The Management of Ovarian Hyperstimulation Syndrome (Green-top Guideline No. 5)",
        authors: "Royal College of Obstetricians and Gynaecologists (RCOG)",
        year: 2016,
        url: "https://www.rcog.org.uk/guidance/browse-all-guidance/green-top-guidelines/the-management-of-ovarian-hyperstimulation-syndrome-green-top-guideline-no-5/",
        evidenceLevel: "guideline",
      },
    ],
  },
});
